/**
 * Sends a POST request to the specified API endpoint.
 * @param {string} url - The API endpoint URL.
 * @param {Object} data - The data payload to send in the request body.
 * @param {Object} headers - Optional headers (default: JSON headers).
 * @returns {Promise<Object>} - The response JSON or an error message.
 */

const baseUrl = "https://videoscaling.chaperoneaiapi.net/"; // Change this for production
// const baseUrl = "http://127.0.0.1:8500/"; // Change this for production


export async function checkLogin(response){

    if (response.status === 401){        
        localStorage.removeItem("access_token");
        return 401
    }
    return response
}




export async function getFileMetaData(endpoint, data) {
    
    try {        
        const url = baseUrl + (endpoint.startsWith('/') ? endpoint.slice(1) : endpoint);


        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json"
        };

        requestHeaders["Authorization"] = `Bearer `;

        
        // Send the request
        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            credentials: "include",  // ✅ Ensure cookies are sent
            body: JSON.stringify(data),  // ✅ Don't send `user_uuid` manually, let FastAPI handle it
        });

        // Handle API errors
        if (!response.ok) {
            const errorText = await response.json();            
            throw new Error(`Error: ${errorText["detail"]}`);
      
        }

        return await response.json();
    } catch (error) {
        console.error("Error posting to API:", error.message);
        return { success: false, message: error.message };
    }
}



export async function getDownloadedFile(endpoint, data) {
    
    try {        
        const url = baseUrl + (endpoint.startsWith('/') ? endpoint.slice(1) : endpoint);


        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json"
        };

        requestHeaders["Authorization"] = `Bearer `;

        
        // Send the request
        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            credentials: "include",  // ✅ Ensure cookies are sent
            body: JSON.stringify(data),  // ✅ Don't send `user_uuid` manually, let FastAPI handle it
        });

        // Handle API errors
        if (!response.ok) {
            const errorText = await response.json();            
            throw new Error(`Error: ${errorText["detail"]}`);
      
        }

        return await response.json();
    } catch (error) {
        console.error("Error posting to API:", error.message);
        return { success: false, message: error.message };
    }
}


export async function postToAPI(endpoint, data, headers = {}) {
    
    try {        
        const url = baseUrl + (endpoint.startsWith('/') ? endpoint.slice(1) : endpoint);


        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };

        requestHeaders["Authorization"] = `Bearer `;

        
        // Send the request
        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            credentials: "include",  // ✅ Ensure cookies are sent
            body: JSON.stringify(data),  // ✅ Don't send `user_uuid` manually, let FastAPI handle it
/*
            body: JSON.stringify({
                ...data,
                user_uuid: userId,  // ✅ Match FastAPI schema
            }),*/
        });

        // Handle API errors
        if (!response.ok) {
            const errorText = await response.json();            
            throw new Error(`Error: ${errorText["detail"]}`);
      
        }

        return await response.json();
    } catch (error) {
        console.error("Error posting to API:", error.message);
        return { success: false, message: error.message };
    }
}

export async function logIn(username, password, headers = {}) {
    try {
        const url = `${baseUrl}token`;

        // Convert username/password into `x-www-form-urlencoded`
        const formData = new URLSearchParams();
        formData.append("username", username);
        formData.append("password", password);

        // Send the request
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                ...headers,
            },
            body: formData.toString(), // Send form data properly
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Login failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data; // { access_token, token_type }
    } catch (error) {
        return { success: false, message: error.message };
    }
}


export async function createUser(full_name, email, password, jobNotification, isThirdParty, headers = {}) {
    try {

        const url = `${baseUrl}user/create`; // Example endpoint, adjust as needed
                
        const payload = {
            full_name: full_name,
            email: email,
            password: password,
            job_notification: jobNotification,
            is_third_party: isThirdParty
        };
        

        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers
        };


        const token = localStorage.getItem("access_token");

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload),
        });

        const data = await response.json(); // Always parse response before returning
        
        if (!response.ok) {
            throw new Error(data.detail || "Failed to create user.");
        }

        return data; // Return the parsed response for further processing


    } catch (error) {
        console.error("API Error: User creation:", error.message);
        return { success: false, message: error.message };
    }
}


export async function sendResetPasswordEmail(email, headers = {}) {
    try {
        
        const url = `${baseUrl}user/reset-password-request`;

        const payload = { email: email };

        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };


        const token = localStorage.getItem("access_token");

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });

        checkLogin(response);

        return response
    } catch (error) {
        console.error("Password reset error:", error.message);
        return { success: false, message: error.message };
    }
}

export async function resetUserPassword(password, temp_token, user_id, headers = {}) {
    try {
        
        const url = `${baseUrl}user/reset-password`;

        const payload = { password: password, token: temp_token, user_id: user_id };

        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };


        const token = localStorage.getItem("access_token");

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });

        checkLogin(response);

        return response;
    } catch (error) {
        console.error("Password reset error:", error.message);
        return { success: false, message: error.message };
    }
}



export async function deleteUser(email, reason, additionalFeedback, headers = {}) {
    try {
        
        const url = `${baseUrl}user/delete-account`;

        const payload = { email: email, reason: reason, additional_feedback: additionalFeedback };

        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };


        const token = localStorage.getItem("access_token");

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });

        checkLogin(response);

        return response;
    } catch (error) {
        console.error("User activity details:", error.message);
        return { success: false, message: error.message };
    }
}


export async function getUserActivityDetails(headers = {}) {
    try {
        const url = `${baseUrl}user/activity-details`;

        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "GET",
            headers: requestHeaders
        });
        
        return checkLogin(response);    
        //return await response.json();
    } catch (error) {
        console.error("User activity details:", error.message);
        return { success: false, message: error.message };
    }
}


export async function getUserTranscripts(headers = {}) {
    try {
        const url = `${baseUrl}user/transcripts`;

        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "GET",
            headers: requestHeaders
        });

        return checkLogin(response);
        
        //return await response.json(); // Expecting { success: true, message: "Reset email sent" }
    } catch (error) {
        console.error("Password reset error:", error.message);
        return { success: false, message: error.message };
    }
}

export async function getUserDetails(headers = {}) {
    try {
        const url = `${baseUrl}user/details`;

        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "GET",
            headers: requestHeaders
        });

        return checkLogin(response);
        //return response; // Expecting { success: true, message: "Reset email sent" }
    } catch (error) {
        console.error("User access details error:", error.message);
        return { success: false, message: error.message };
    }    
}


export async function updateUser(fullName, email, jobNotification, headers = {}) {
    try {
        const url = `${baseUrl}user/update`;        

        const payload = { full_name: fullName, email: email, job_notification: jobNotification};
        
        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,            
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });
        return checkLogin(response);
        
    } catch (error) {
        console.error("User access details error:", error.message);
        return { success: false, message: error.message };
    }    

}


export async function downloadFile(fileName, headers = {}) {
    try {

        const apiurl = `${baseUrl}transcript/download/${fileName}`; // Example endpoint, adjust as needed

        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(apiurl, {
            method: "GET",
            headers: requestHeaders
        });
    
        checkLogin(response);

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
    
        // Convert response data into a blob
        const blob = await response.blob();
    
        // Create a URL for the blob object
        const url = window.URL.createObjectURL(blob);
    
        // Create a temporary <a> element to trigger the download
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName; // filename suggested to the browser
        document.body.appendChild(a);
        a.click();
    
        // Clean up: remove the element and revoke the object URL
        a.remove();
        window.URL.revokeObjectURL(url);
        } catch (error) {
        console.error("Error downloading file:", error);
        }
  }
  

export async function sendFeedback(jobId, rating, headers = {}) {
    try {
        const url = `${baseUrl}transcript/feedback`;        

        const payload = { job_id: jobId, feedback_score: rating };
        
        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,            
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });
        return checkLogin(response);
    } catch (error) {
        console.error("User access details error:", error.message);
        return { success: false, message: error.message };
    }    

}

export async function deleteTask(jobId, headers = {}) {
    try {
        const url = `${baseUrl}transcript/remove`;        

        const payload = { job_id: jobId };
        
        // Get the token from localStorage (assuming it's stored there after login)
        const token = localStorage.getItem("access_token");

        // Build the request headers
        const requestHeaders = {
            "Content-Type": "application/json",
            ...headers,            
        };

        requestHeaders["Authorization"] = `Bearer ${token}`;

        const response = await fetch(url, {
            method: "POST",
            headers: requestHeaders,
            body: JSON.stringify(payload)
        });
        return checkLogin(response);
    } catch (error) {
        console.error("User access details error:", error.message);
        return { success: false, message: error.message };
    }        
}
